import React, { useState, useContext, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify';
import GenericModal from '../../../components/modal/GenericModal';
import GenericUploadFileV2 from '../../../components/GenericUploadFile/GenericUploadFileV2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { downloadFile } from '../../../consumers/backendConsumer';
import ModalEnabled from '../../../components/modalComponents/ModalEnabled';
import ActionGenericModal from '../../../components/modal/ActionGenericModal';
import ProbarCorreoConvocatoriaModal from './ProbarCorreoConvocatoriaModal';
import { AuthContext } from '../../../hooks/context/authContext';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { mapDropDownOptionsFromBackendToSelectOptionsNeededForLibrary, mapSedesToOptionsWithRegion, getRegionOptionsFromSedes } from '../../../helper/utils';
import { getIdEmpleadoSesion } from '../utils/sesion';
import AsyncSelectInput from '../../../components/Select/AsyncSelectInput';
import {
    crearConvocatoriaAPI,
    actualizarConvocatoriaAPI,
    guardarFormularioConvocatoriaAPI,
    publicarConvocatoriaAPI,
    getTPCargosAPI,
    getTPSedesAPI,
    getTPAlternativasAPI,
    getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI,
    getFormularioConvocatoriaAPI,
    listarArchivosApoyoAPI,
    guardarArchivoApoyoAPI,
    eliminarArchivoApoyoAPI,
} from '../../../consumers/backendApisUrls';
import InputText from '../../../components/modalComponents/InputText';
import InputSelect from '../../../components/modalComponents/InputSelect';
import InputMultiSelect from '../../../components/modalComponents/InputMultiSelect';
import InputDate from '../../../components/modalComponents/InputDate';
import InputEditorHtml from '../../../components/modalComponents/InputEditorHtml';
import InputNumber from '../../../components/modalComponents/InputNumber';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Stepper from '../components/Stepper';
import RecapField from '../components/RecapField';
import { getRecapLabels, parseFechaDetalleToIso, mapTipoProcesoTextoToCodigo, aInicioDelDia, aFinDelDia, formatFechaHoraLocal, parseFechaHoraLocal } from './utils/convocatoriaRecap';
import DeclaracionesStep from './steps/DeclaracionesStep';
import FeedbackStep from './steps/FeedbackStep';
import ResumenStep from './steps/ResumenStep';

// ──────────────────────────────────────────────────────────────────────────
// Modal "Nueva convocatoria — Masivo". Wizard calcado del boceto a mano
// compartido por Jody (pasos: Especificación, Segmentación, Declaraciones,
// Feedback, Resumen), usando la misma forma de visualizar pasos que
// DatosPersonalesEditarGlobal.jsx (círculos numerados + línea, ver
// ../components/Stepper.jsx) y los mismos componentes de input reutilizables
// de esa pantalla (InputText/InputSelect/InputDate/etc. de components/modalComponents).
//
// Todavía no hay backend para este módulo, así que por ahora:
// - El estado del formulario es local (sin submitForm/validación real todavía).
// - "Subir Documento 1/2" son placeholders (no suben a S3 todavía) — cuando exista
//   el endpoint de adjuntos (tabla ARCHIVO) se reemplazan por InputUploadFile +
//   GenericUploadFileV2, igual que el resto de la app.
// - "Responsable" es un <select> con lista mock; cuando exista el API de búsqueda
//   de empleados se reemplaza por AsyncSelectInput (mismo patrón usado en
//   ReasignarResponsable.jsx de /cuadroRequerimientosListar).
// - Los 5 pasos ya están diseñados (Especificación, Segmentación, Declaraciones,
//   Feedback, Resumen).
// - El "Tipo" de esta convocatoria es TipoModalidad='M' (Masivo) de forma implícita,
//   porque el usuario ya eligió "Masivo" en el dropdown "Nueva convocatoria" de la
//   bandeja — por eso no aparece un campo para elegirlo aquí.
// - El footer cambia en el último paso (Resumen, mockup 26/08/2026): se agrega
//   "Enviar correo prueba" y el botón principal pasa a decir "Publicar
//   convocatoria" en vez de "Guardar y Continuar". No hay botón "Anterior" ni
//   "Vista previa" — para retroceder se hace clic en el número de paso del
//   Stepper (ya soportado desde el principio, ver components/Stepper.jsx).
// - Modo edición (03/09/2026, GestionTrasladosYPromociones.jsx > "Editar"): este
//   componente puede recibir una prop `detalleConvocatoria`, con la respuesta de
//   GetDetalleConvocatoria (.NET), para abrir el wizard ya ubicado en el paso en
//   que se quedó la convocatoria (`Etapa`, 1 a 1 con el índice de STEPS) y con
//   los datos generales (Nombre/Tipo/Vigencia) precargados — son justamente los
//   que muestra RecapField en casi todos los pasos. GetDetalleConvocatoria es
//   deliberadamente liviano (no trae Cargo/Regiones/Sedes/Declaraciones/
//   Feedback), así que esos recaps quedan en blanco ("-") al editar por ahora;
//   traerlos completos requeriría el GetConvocatoria más pesado (ConvocatoriaDto)
//   — queda pendiente para cuando se pida hidratar el wizard completo en edición.
// ──────────────────────────────────────────────────────────────────────────

const SECTION_NAME = "NuevaConvocatoriaMasivo";

const STEPS = ["Especificación", "Segmentación", "Declaraciones", "Feedback", "Resumen"];

const TIPO_PROCESO_OPTIONS = [
    { value: "T", label: "Traslado" },
    { value: "P", label: "Promoción" },
];

// Toolbar reducido SOLO para "Lineamientos" de este wizard (a pedido explicito):
// sin listas/link/imagen, solo negrita/italica/subrayado + Normal/H1-H3 + deshacer.
// EditorDraft.jsx sigue usando su toolbar completo por default para cualquier otro
// InputEditorHtml del resto de la app — esto no lo toca.
const TOOLBAR_LINEAMIENTOS = {
    options: ['inline', 'blockType', 'history'],
    inline: { options: ['bold', 'italic', 'underline'] },
    blockType: { options: ['Normal', 'H1', 'H2', 'H3'] },
};

// IdsRegiones/IdsSedes llegan en el `row` de la grilla como string separado por
// comas ("1,2,3"). Se convierten a array de NUMEROS, no de strings: los
// catalogos vienen de BaseDropdownViewModel (int Value), asi que el
// `options.filter(o => IdsRegiones.includes(o.value))` de InputMultiSelect y de
// getRecapLabels compara contra numeros — con strings no casaba nunca y las
// Regiones/Sedes guardadas se veian vacias al reabrir la convocatoria.
const toArrayConComas = (value) => {
    const lista = Array.isArray(value)
        ? value
        : (typeof value === "string" ? value.split(",") : []);
    return lista
        .map(v => String(v).trim())
        .filter(v => v !== "")
        .map(Number)
        .filter(v => !Number.isNaN(v));
};

// GetFormularioConvocatoria devuelve los DTO .NET en PascalCase
// (ConvocatoriaDeclaracionDto / ConvocatoriaPreguntaDto); los pasos 3 y 4 del
// wizard trabajan con su propia forma local ({Titulo,Detalle} /
// {Texto,TipoPregunta,Alternativas}). Estos dos mappers hacen la traduccion.
//
// El IdTemp de las filas hidratadas es NEGATIVO a proposito:
// DeclaracionesStep/FeedbackStep asignan IdTemp con un contador de modulo que
// arranca en 1, y si la fila de BD usara 1..n la primera declaracion agregada a
// mano chocaria con ella (misma key de React, y editar/eliminar afectaria a las
// dos).
const mapDeclaracionesDesdeBackend = (declaraciones) => (declaraciones ?? []).map((d, index) => ({
    IdTemp: -(index + 1),
    Titulo: d.TituloDeclaracion ?? "",
    Detalle: d.DetalleDeclaracion ?? "",
}));

// TP_AlternativaPregunta solo guarda IdAlternativa; el texto sale del catalogo
// (dbo.DataType Discriminator='AlternativasTYP'), por eso hace falta
// `alternativaOptions` ya cargado para reconstruir el {value,label} que esperan
// FeedbackStep y TransferList.
const mapPreguntasDesdeBackend = (preguntas, alternativaOptions) => (preguntas ?? []).map((p, index) => ({
    IdTemp: -(index + 1),
    Texto: p.TextoPregunta ?? "",
    TipoPregunta: p.TipoPregunta ?? "A",
    EsVisible: p.EsVisible ?? true,
    Alternativas: (p.Alternativas ?? []).map(a =>
        alternativaOptions.find(o => o.value === a.IdAlternativa)
        ?? { value: a.IdAlternativa, label: String(a.IdAlternativa ?? "") }),
}));

// Mapea el estado del wizard (declaraciones/preguntas, UI-local) al shape de los
// DTO .NET (ConvocatoriaDeclaracionDto / ConvocatoriaPreguntaDto / ConvocatoriaAlternativaPreguntaDto).
// El backend .NET camelCasea el objeto completo antes de reenviarlo a Java.
const buildFormularioPayload = (declaraciones, preguntas) => ({
    Declaraciones: (declaraciones || []).map(d => ({
        TituloDeclaracion: d.Titulo,
        DetalleDeclaracion: d.Detalle,
    })),
    Preguntas: (preguntas || []).map((p, index) => ({
        TextoPregunta: p.Texto,
        EsVisible: p.EsVisible,
        TipoPregunta: p.TipoPregunta,
        Orden: index + 1,
        Alternativas: (p.Alternativas || []).map(a => ({
            IdAlternativa: a?.value ?? null,
        })),
    })),
});

// CA-07 / R-07: maximo 2 archivos de apoyo por convocatoria.
// El limite tambien se valida server-side en TrasladosPromocionesService.
const MAX_ARCHIVOS_APOYO = 2;
const EXTENSIONES_ARCHIVOS_APOYO = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "ppt", "pptx", "xls", "xlsx"];

// A partir de la respuesta de GetDetalleConvocatoria (o undefined, en modo creación)
// arma el formValues inicial del wizard: solo los campos generales que esa API
// expone (ver comentario de arriba) — Cargo/Regiones/Sedes/etc. quedan sin
// precargar en modo edición.
const buildInitialFormValues = (detalle) => {
    if (!detalle) return { TipoProceso: "T" };
    return {
        IdConvocatoria: detalle.IdConvocatoria,
        NombreConvocatoria: detalle.NombreConvocatoria,
        TipoProceso: mapTipoProcesoTextoToCodigo(detalle.TipoProceso) ?? "T",
        FechaHoraInicio: parseFechaDetalleToIso(detalle.FechaHoraInicio),
        FechaHoraFin: parseFechaDetalleToIso(detalle.FechaHoraFin),
    };
}

// Recibe AMBAS fuentes de precarga:
//  - `detalleConvocatoria` (GetDetalleConvocatoria, AGA266): trae la Etapa para
//    reabrir el wizard en el paso guardado, y los datos generales.
//  - `convocatoria` (el `row` camelCase de la grilla): trae MAS campos (cargo,
//    regiones, sedes, antiguedad, responsable, lineamientos) y fechas con hora,
//    por eso su useEffect corre despues y completa lo que el detalle no expone.
// `modoEdicion` distingue Editar (actualiza) de Copiar (crea una nueva).
const NuevaConvocatoriaMasivo = ({ toggleModal, refreshPage, convocatoria = null, modoEdicion = false, detalleConvocatoria = null }) => {
    const { state } = useContext(AuthContext);
    // Etapa es 1 a 1 con el índice (1-based) de STEPS: Etapa=2 → Segmentación, Etapa=5 → Resumen.
    const [currentStep, setCurrentStep] = useState(() => detalleConvocatoria?.Etapa || 1);
    const [formValues, setFormValues] = useState(() => buildInitialFormValues(detalleConvocatoria));
    const [declaraciones, setDeclaraciones] = useState([]);
    const [preguntas, setPreguntas] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalConfirmacion, setModalConfirmacion] = useState({ isEnable: false });
    const [modalCorreoPrueba, setModalCorreoPrueba] = useState({ isEnable: false });
    const [cargoOptions, setCargoOptions] = useState([]);
    const [sedeOptions, setSedeOptions] = useState([]);
    // Regiones derivadas de las Sedes ya cargadas (idRegion/desRegion, ver
    // mapSedesToOptionsWithRegion) en vez de pedirlas aparte a GetRegiones: evita
    // una llamada extra y asegura que solo aparezcan regiones con alguna sede.
    const regionOptions = useMemo(() => getRegionOptionsFromSedes(sedeOptions), [sedeOptions]);
    const [alternativaOptions, setAlternativaOptions] = useState([]);
    const [managePostRequest] = useManagePostRequest();
    const [executeGet] = useGetRequest();
    // CA-07: archivos de apoyo. En Creacion viven en memoria y viajan en el
    // payload (ArchivosApoyo); en Edicion se persisten/eliminan de inmediato
    // contra el backend, porque la convocatoria ya tiene IdConvocatoria.
    const [archivosApoyo, setArchivosApoyo] = useState([]);
    const [subiendoArchivo, setSubiendoArchivo] = useState(false);

    // Id de la convocatoria creada por este wizard (modo Creación). Una vez que
    // existe, los guardados posteriores ACTUALIZAN en vez de crear: sin esto,
    // "Guardar Borrador" o "Guardar y Continuar" varias veces creaba una
    // convocatoria nueva en cada clic.
    const [idConvocatoriaCreada, setIdConvocatoriaCreada] = useState(null);

    // Id efectivo: el de Edición, o el creado en esta sesión del wizard.
    const idConvocatoriaActual = (modoEdicion ? convocatoria?.idConvocatoria : null) ?? idConvocatoriaCreada;

    // Catalogos + (si la convocatoria ya existe) el formulario ya guardado.
    // Van en el MISMO efecto y en este orden porque las alternativas de cada
    // pregunta se guardan solo por IdAlternativa: sin el catalogo cargado no se
    // puede reconstruir su texto.
    useEffect(() => {
        (async () => {
            const [cargos, sedes, alternativas] = await Promise.all([
                executeGet(getTPCargosAPI()),
                executeGet(getTPSedesAPI()),
                executeGet(getTPAlternativasAPI()),
            ]);
            const alternativaOpciones = mapDropDownOptionsFromBackendToSelectOptionsNeededForLibrary(alternativas?.data ?? []);
            setCargoOptions(mapDropDownOptionsFromBackendToSelectOptionsNeededForLibrary(cargos?.data ?? []));
            setSedeOptions(mapSedesToOptionsWithRegion(sedes?.data ?? []));
            setAlternativaOptions(alternativaOpciones);

            // Declaraciones y preguntas ya guardadas (Editar, y tambien Copiar:
            // duplicar una convocatoria incluye su formulario).
            //
            // Esto no es solo para poder verlas: GuardarFormularioConvocatoria
            // hace delete + reinsert con lo que tenga el wizard en memoria, asi
            // que sin precargarlas cualquier "Guardar y Continuar" desde el
            // modo Edicion BORRABA las declaraciones y preguntas configuradas.
            const idExistente = convocatoria?.idConvocatoria;
            if (!idExistente) return;
            const formulario = await executeGet(getFormularioConvocatoriaAPI(idExistente));
            if (!formulario?.data) return;
            setDeclaraciones(mapDeclaracionesDesdeBackend(formulario.data.Declaraciones));
            setPreguntas(mapPreguntasDesdeBackend(formulario.data.Preguntas, alternativaOpciones));
        })();
    }, []);

    // Precarga desde el `row` camelCase de la grilla (Editar/Copiar). IdsRegiones/IdsSedes
    // vienen como string separado por comas; se convierten a array para los InputMultiSelect.
    useEffect(() => {
        if (convocatoria) {
            setFormValues({
                NombreConvocatoria: convocatoria.nombreConvocatoria ?? "",
                TipoProceso: convocatoria.tipoProceso ?? "T",
                IdEmpleadoResponsable: convocatoria.idEmpleadoResponsable ?? null,
                NombreEmpleadoResponsable: convocatoria.nombreEmpleadoResponsable ?? "",
                Lineamientos: convocatoria.lineamientos ?? "",
                IdCargo: convocatoria.idCargo ?? null,
                IdsRegiones: toArrayConComas(convocatoria.idsRegiones),
                IdsSedes: toArrayConComas(convocatoria.idsSedes),
                AnioAntiguedad: convocatoria.anioAntiguedad ?? null,
                MesAntiguedad: convocatoria.mesAntiguedad ?? null,
                FechaHoraInicio: convocatoria.fechaHoraInicio?.slice(0, 10) ?? "",
                // Fecha de Término (30/09/2026, a pedido de Jody): a diferencia de
                // FechaHoraInicio, esta SÍ se queda con la hora completa (sin
                // slice) — es lo que permite que el DatePicker de abajo
                // reconstruya la hora que se eligió la última vez que se guardó.
                FechaHoraFin: convocatoria.fechaHoraFin ?? "",
            });
        }
    }, [convocatoria]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // InputSelect (react-select) entrega el objeto completo {value,label},
        // pero el resto del wizard trabaja con primitivos: el render compara
        // `o.value === formValues.X` y el payload hace numero(X). Sin desempaquetar,
        // el select se ve vacio y al backend le llega un objeto.
        // No aplica a InputMultiSelect (ya devuelve un array de valores) ni a los
        // inputs de texto/fecha/numero/editor, que mandan primitivos.
        const valor = (value && typeof value === "object" && !Array.isArray(value) && "value" in value)
            ? value.value
            : value;
        setFormValues(lastData => ({ ...lastData, [name]: valor }));
    }

    // AsyncSelectInput reenvia el (value, actionMeta) de react-select: al limpiar
    // llega `null`, al elegir llega el objeto {value,label} completo. Se guarda
    // tambien el label porque es lo unico que el AsyncSelect puede mostrar —
    // el endpoint es de autocompletado y no resuelve un id suelto a su nombre.
    const handleChangeResponsable = (option) => {
        setFormValues(lastData => ({
            ...lastData,
            IdEmpleadoResponsable: option?.value ?? null,
            NombreEmpleadoResponsable: option?.label ?? "",
        }));
    }

    // Valor que pinta el AsyncSelect (prop `defaultValue`). Sin esto, al reabrir
    // una convocatoria guardada el campo Responsable aparecia vacio.
    const responsableSeleccionado = formValues.IdEmpleadoResponsable
        ? { value: formValues.IdEmpleadoResponsable, label: formValues.NombreEmpleadoResponsable ?? "" }
        : null;

    const numero = (v) => (v === "" || v === undefined || v === null) ? null : Number(v);

    // IdsRegiones/IdsSedes llegan como arrays de valores; el backend los espera
    // como string de ids separados por comas.
    const getPayload = () => {
        const idsRegiones = Array.isArray(formValues.IdsRegiones) ? formValues.IdsRegiones.join(",") : formValues.IdsRegiones;
        const idsSedes = Array.isArray(formValues.IdsSedes) ? formValues.IdsSedes.join(",") : formValues.IdsSedes;
        return {
            NombreConvocatoria: formValues.NombreConvocatoria,
            TipoProceso: formValues.TipoProceso,
            TipoModalidad: "M",
            IdEmpleadoResponsable: numero(formValues.IdEmpleadoResponsable) ?? getIdEmpleadoSesion(state),
            Lineamientos: formValues.Lineamientos,
            IdCargo: numero(formValues.IdCargo),
            IdsRegiones: idsRegiones,
            IdsSedes: idsSedes,
            AnioAntiguedad: numero(formValues.AnioAntiguedad),
            MesAntiguedad: numero(formValues.MesAntiguedad),
            FechaHoraInicio: aInicioDelDia(formValues.FechaHoraInicio),
            // Fecha de Término (30/09/2026, a pedido de Jody): ahora se elige con
            // hora propia (DatePicker más abajo guarda "YYYY-MM-DDTHH:mm:ss" via
            // formatFechaHoraLocal). Si ya trae su propia hora se manda tal cual;
            // aFinDelDia() queda solo de fallback para valores viejos que todavía
            // son fecha sola (ej. los que llegan de GetDetalleConvocatoria, que no
            // expone hora — ver buildInitialFormValues).
            FechaHoraFin: formValues.FechaHoraFin?.includes("T") ? formValues.FechaHoraFin : aFinDelDia(formValues.FechaHoraFin),
            ...buildFormularioPayload(declaraciones, preguntas),
            // CA-07: solo los adjuntos aun no persistidos (los que ya tienen
            // IdArchivo se guardaron via GuardarArchivoApoyo en modo Edicion).
            ArchivosApoyo: archivosApoyo.filter(o => !o?.IdArchivo),
        };
    }

    // CA-07: agrega un archivo de apoyo. `s3Response` es el FileViewModel que
    // devuelve Home/UploadFile (el archivo ya esta en S3, carpeta temporary).
    const handleAgregarArchivoApoyo = (s3Response) => {
        if (!s3Response?.Path) return;

        if (archivosApoyo.length >= MAX_ARCHIVOS_APOYO) {
            toast.error(`Solo se permiten ${MAX_ARCHIVOS_APOYO} archivos de apoyo por convocatoria.`);
            return;
        }

        // La convocatoria ya existe (Edición o ya guardada): se persiste de inmediato.
        if (idConvocatoriaActual) {
            managePostRequest(
                guardarArchivoApoyoAPI(idConvocatoriaActual),
                { File: s3Response },
                (res) => setArchivosApoyo(lastData => [...lastData, res?.data ?? s3Response]),
                false
            );
            return;
        }

        // Creacion: queda en memoria y viaja en el payload de CrearConvocatoria.
        setArchivosApoyo(lastData => [...lastData, s3Response]);
    }

    const handleEliminarArchivoApoyo = (archivo, index) => {
        // Ya persistido: se elimina de S3 y de dbo.Archivo.
        if (archivo?.IdArchivo) {
            managePostRequest(
                eliminarArchivoApoyoAPI(archivo.IdArchivo),
                {},
                () => setArchivosApoyo(lastData => lastData.filter((_, i) => i !== index)),
                false
            );
            return;
        }

        setArchivosApoyo(lastData => lastData.filter((_, i) => i !== index));
    }

    const handleDescargarArchivoApoyo = (archivo) => {
        downloadFile(state.token, archivo.Path, archivo.OriginalFileName);
    }

    // CA-07: en Edicion se traen los adjuntos ya guardados. En Copiar NO se
    // copian los archivos (el modelo asocia cada Archivo a una convocatoria por
    // IdEntidad; duplicarlos requeriria copiar tambien los objetos en S3).
    // Al existir la convocatoria (Edición, o tras el primer guardado en
    // Creación) se recargan los adjuntos ya persistidos: así los que estaban en
    // memoria pasan a tener IdArchivo y su papelera borra también en servidor.
    useEffect(() => {
        if (!idConvocatoriaActual) return;
        (async () => {
            const respuesta = await executeGet(listarArchivosApoyoAPI(idConvocatoriaActual));
            setArchivosApoyo(respuesta?.data ?? []);
        })();
    }, [idConvocatoriaActual]);

    const handleCancelar = () => {
        toggleModal();
    }

    // Retrocede un paso SIN guardar. Es el mismo movimiento que ya permitia el
    // Stepper (clic en el numero de un paso ya visitado), pero como boton
    // visible en el footer. Lo cargado en el paso actual sigue vivo en
    // formValues/declaraciones/preguntas, asi que no se pierde al volver a
    // avanzar.
    const handleAtras = () => {
        setCurrentStep(lastStep => Math.max(1, lastStep - 1));
    }

    /**
     * Persiste el estado actual del wizard y luego ejecuta `onSuccess(id)`.
     *  - Si la convocatoria todavía no existe: la crea en borrador (66) y
     *    guarda su id, para que los guardados siguientes actualicen.
     *  - Si ya existe: actualiza la cabecera y reescribe el formulario
     *    (ActualizarConvocatoria solo toca la cabecera, por eso van los dos).
     * Lo usan Guardar Borrador, Guardar y Continuar y Publicar.
     */
    const persistirConvocatoria = (onSuccess) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const alFallar = () => setIsSubmitting(false);
        const id = idConvocatoriaActual;

        if (id) {
            managePostRequest(actualizarConvocatoriaAPI(id), getPayload(), () => {
                managePostRequest(guardarFormularioConvocatoriaAPI(id), buildFormularioPayload(declaraciones, preguntas), () => {
                    setIsSubmitting(false);
                    onSuccess && onSuccess(id);
                }, false, alFallar, true);
            }, false, alFallar, true);
            return;
        }

        managePostRequest(crearConvocatoriaAPI(), getPayload(), (response) => {
            const nuevoId = response?.data?.IdConvocatoria ?? null;
            setIdConvocatoriaCreada(nuevoId);
            setIsSubmitting(false);
            onSuccess && onSuccess(nuevoId);
        }, false, alFallar, true);
    }

    // CA-08/11/15 + R-04: guarda el avance sin exigir completar las demás etapas.
    // El toast de exito es manual (persistirConvocatoria llama a managePostRequest
    // con showToastSuccess=false) porque ese mismo helper lo comparten Guardar y
    // Continuar / Publicar, que no deben mostrar este mensaje de borrador.
    const handleGuardarBorrador = () => {
        persistirConvocatoria(() => {
            toast.success('Guardado como borrador exitoso');
            refreshPage && refreshPage();
        });
    }

    // Enviar correo prueba = levanta un modal chico (solo correo + botón "Enviar")
    // que llama a ProbarCorreoConvocatoria. Ver ProbarCorreoConvocatoriaModal.jsx.
    const handleEnviarCorreoPrueba = () => {
        // Se usa idConvocatoriaActual (id de Edicion, o el creado en esta sesion)
        // y NO formValues.IdConvocatoria: el useEffect que precarga desde el `row`
        // reemplaza formValues completo y borraria ese campo, y en modo Creacion
        // nunca se setea. Sin id, ProbarCorreoConvocatoria no tiene que enviar.
        if (!idConvocatoriaActual) {
            toast.error("Guarda la convocatoria antes de enviar el correo de prueba.");
            return;
        }
        setModalCorreoPrueba({
            isEnable: true,
            component: ProbarCorreoConvocatoriaModal,
            data: { idConvocatoria: idConvocatoriaActual },
        });
    }

    // Validacion del paso 2 (Segmentacion): Sedes y Antigüedad son obligatorios
    // (a diferencia del resto del wizard, que solo marca "required" visualmente
    // sin bloquear). No se llama desde Guardar Borrador a proposito: R-04 exige
    // poder guardar el avance sin completar todas las etapas.
    const validarSegmentacion = () => {
        if (!formValues.IdCargo) {
            toast.error("Debes seleccionar un cargo.");
            return false;
        }
        if (!formValues.IdsSedes?.length) {
            toast.error("Debes seleccionar al menos una sede.");
            return false;
        }
        // Las sedes sin Region (idRegion=null, ej. BACK OFFICE) nunca van a
        // "calzar" con ninguna Region elegida — si el usuario ya eligio alguna de
        // esas, exigirle ademas una Region no tiene sentido.
        const tieneSedeSinRegion = formValues.IdsSedes.some(id =>
            sedeOptions.find(o => o.value === id)?.idRegion == null
        );
        if (!tieneSedeSinRegion && !formValues.IdsRegiones?.length) {
            toast.error("Debes seleccionar al menos una región.");
            return false;
        }
        if (formValues.AnioAntiguedad === null || formValues.AnioAntiguedad === undefined || formValues.AnioAntiguedad === ""
            || formValues.MesAntiguedad === null || formValues.MesAntiguedad === undefined || formValues.MesAntiguedad === "") {
            toast.error("Debes completar la antigüedad (años y meses).");
            return false;
        }
        return true;
    }

    // CA-08/11/15 + R-16: "Guardar y continuar" debe GUARDAR la etapa actual
    // antes de dar acceso a la siguiente (R-03: la información registrada debe
    // seguir asociada al proceso y disponible en las etapas posteriores).
    const handleGuardarYContinuar = () => {
        if (currentStep === 2 && !validarSegmentacion()) return;
        persistirConvocatoria(() => {
            refreshPage && refreshPage();
            setCurrentStep(lastStep => lastStep + 1);
        });
    }

    // Publicar = persistir primero (crea el borrador 66 si aun no existe, o
    // actualiza con lo cargado en el paso 5) y luego pedir confirmacion con el
    // ActionGenericModal de AGA266, que es quien llama a PublicarConvocatoria (67).
    // Se revalida Segmentacion aca tambien porque el Stepper permite saltar
    // directo a un paso ya visitado (ej. Resumen) sin pasar de nuevo por
    // handleGuardarYContinuar.
    const handlePublicarConvocatoria = () => {
        if (!validarSegmentacion()) return;
        persistirConvocatoria((id) => {
            if (!id) return;
            setModalConfirmacion({
                isEnable: true,
                component: ActionGenericModal,
                data: {
                    title: "Publicar Convocatoria",
                    actionLabel: "publicar",
                    api: publicarConvocatoriaAPI,
                    // El id va en el body (POST), no en la URL — por eso no se pasa `id`.
                    payload: { IdConvocatoria: id },
                    name: formValues.NombreConvocatoria,
                    // Al publicar con exito se cierra tambien el wizard completo.
                    externalCallback: toggleModal,
                },
            });
        });
    }

    return (
        <GenericModal showModal={toggleModal} titulo={(modoEdicion || detalleConvocatoria) ? "Editar Convocatoria — Masivo" : "Nueva Convocatoria — Masivo"} width="1100px">
            <ModalEnabled modalEnabled={modalConfirmacion} setModalEnabled={setModalConfirmacion} refreshPage={refreshPage} />
            <ModalEnabled modalEnabled={modalCorreoPrueba} setModalEnabled={setModalCorreoPrueba} refreshPage={refreshPage} />

            <div className="modal-body">
                <Stepper steps={STEPS} currentStepNumber={currentStep} setCurrentStepNumber={setCurrentStep} />

                {currentStep === 1 &&
                    <EspecificacionStep formValues={formValues} handleChange={handleChange} handleChangeResponsable={handleChangeResponsable}
                        responsableSeleccionado={responsableSeleccionado}
                        archivosApoyo={archivosApoyo} onAgregarArchivo={handleAgregarArchivoApoyo}
                        onEliminarArchivo={handleEliminarArchivoApoyo} onDescargarArchivo={handleDescargarArchivoApoyo}
                        subiendoArchivo={subiendoArchivo} setSubiendoArchivo={setSubiendoArchivo} />
                }
                {currentStep === 2 &&
                    <SegmentacionStep formValues={formValues} handleChange={handleChange}
                        tipoProcesoOptions={TIPO_PROCESO_OPTIONS} cargoOptions={cargoOptions} regionOptions={regionOptions} sedeOptions={sedeOptions} />
                }
                {currentStep === 3 &&
                    <DeclaracionesStep formValues={formValues} declaraciones={declaraciones} setDeclaraciones={setDeclaraciones}
                        tipoProcesoOptions={TIPO_PROCESO_OPTIONS} cargoOptions={cargoOptions} regionOptions={regionOptions} sedeOptions={sedeOptions} />
                }
                {currentStep === 4 &&
                    <FeedbackStep formValues={formValues} preguntas={preguntas} setPreguntas={setPreguntas}
                        tipoProcesoOptions={TIPO_PROCESO_OPTIONS} cargoOptions={cargoOptions} regionOptions={regionOptions} sedeOptions={sedeOptions}
                        alternativaOptions={alternativaOptions} />
                }
                {currentStep === 5 &&
                    <ResumenStep formValues={formValues}
                        tipoProcesoOptions={TIPO_PROCESO_OPTIONS} cargoOptions={cargoOptions} regionOptions={regionOptions} sedeOptions={sedeOptions} />
                }

                <div className={`modal-footer d-flex pl-0 ${currentStep === STEPS.length ? "justify-content-end" : "justify-content-between"}`} style={{ borderTop: "1px solid #dee2e6", marginTop: "1.5rem" }}>
                    {currentStep !== STEPS.length &&
                        <button type="button" className="btn generic-button-buscador" onClick={handleGuardarBorrador}>
                            Guardar Borrador
                        </button>
                    }
                    <div className="d-flex" style={{ gap: "12px" }}>
                        {currentStep > 1 &&
                            <button type="button" className="btn generic-button-buscador" onClick={handleAtras}>
                                Atrás
                            </button>
                        }
                        {currentStep === STEPS.length &&
                            <button type="button" className="btn generic-button-buscador" onClick={handleEnviarCorreoPrueba}>
                                Enviar correo prueba
                            </button>
                        }
                        <button type="button" className="btn generic-button-buscador" onClick={handleCancelar}>
                            Cancelar
                        </button>
                        {currentStep < STEPS.length
                            ? <button type="button" className="btn generic-button-buscador generic-button-hover-verde green-innova-background" disabled={isSubmitting} onClick={handleGuardarYContinuar}>
                                Guardar y Continuar
                            </button>
                            : <button type="button" className="btn generic-button-buscador generic-button-hover-verde green-innova-background" disabled={isSubmitting} onClick={handlePublicarConvocatoria}>
                                Publicar convocatoria
                            </button>
                        }
                    </div>
                </div>
            </div>
        </GenericModal>
    )
}

export default NuevaConvocatoriaMasivo

// ──────────────────────────────────────────────────────────────────────────
// Paso 1 — Especificación
// ──────────────────────────────────────────────────────────────────────────
const EspecificacionStep = ({ formValues, handleChange, handleChangeResponsable, responsableSeleccionado, archivosApoyo,
    onAgregarArchivo, onEliminarArchivo, onDescargarArchivo, subiendoArchivo, setSubiendoArchivo }) => (
    <div className="row">
        <div className="col-sm-6">
            <InputSelect label="Tipo" name="TipoProceso" sectionName={SECTION_NAME}
                options={TIPO_PROCESO_OPTIONS}
                value={TIPO_PROCESO_OPTIONS.find(o => o.value === formValues.TipoProceso)}
                onChange={handleChange}
                direction="column" isRequired />

            <InputText label="Nombre" name="NombreConvocatoria" sectionName={SECTION_NAME}
                value={formValues.NombreConvocatoria}
                onChange={handleChange}
                direction="column" isRequired />

            <InputDate label="Fecha de Inicio" name="FechaHoraInicio" sectionName={SECTION_NAME}
                value={formValues.FechaHoraInicio}
                onChange={handleChange}
                direction="column" isRequired />

            {/* Fecha de Término (30/09/2026, a pedido de Jody): a diferencia de
                Fecha de Inicio, esta SÍ permite elegir hora — mismo control
                (DatePicker + showTimeSelect) que usa Entrevista Detalle
                (EntrevistaPostulante.jsx). formValues.FechaHoraFin se sigue
                guardando como string ("YYYY-MM-DD" o "YYYY-MM-DDTHH:mm:ss"),
                igual que el resto del wizard (getRecapLabels/formatFecha lo
                esperan así) — parseFechaHoraLocal/formatFechaHoraLocal hacen la
                conversión ida y vuelta con el Date que pide el DatePicker. */}
            <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                <label className="modal-label d-flex required">Fecha de Término</label>
                <DatePicker className="form-control" name="FechaHoraFin"
                    selected={parseFechaHoraLocal(formValues.FechaHoraFin)}
                    onChange={fecha => handleChange({ target: { name: "FechaHoraFin", value: formatFechaHoraLocal(fecha) } })}
                    showTimeSelect
                    timeIntervals={15}
                    timeCaption="Hora"
                    dateFormat="dd/MM/yyyy HH:mm"
                    placeholderText="dd/mm/aaaa --:--" />
            </div>

            <div className="form-group">
                <label className="modal-label d-flex">Responsable</label>
                <AsyncSelectInput
                    api={getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI}
                    handleSelectInputChange={handleChangeResponsable}
                    defaultValue={responsableSeleccionado}
                    placeholder="Buscar responsable..."
                />
            </div>
        </div>

        <div className="col-sm-6">
            {/* CA-06: los lineamientos admiten formato. Se guardan como HTML en
                TP_Convocatoria.Lineamientos (nvarchar(max)) y se renderizan como
                HTML en el detalle y en el modal de postulación. */}
            <InputEditorHtml label="Lineamientos" name="Lineamientos" sectionName={SECTION_NAME}
                value={formValues.Lineamientos}
                onChange={handleChange}
                direction="column" minHeight="11rem"
                toolbarOptions={TOOLBAR_LINEAMIENTOS} />

            {/* CA-07: hasta 2 archivos de apoyo (tutoriales, flyers, etc.).
                Cada uno se puede descargar y eliminar individualmente. */}
            <div className="mt-3">
                <label className="modal-label d-flex">
                    Archivos de apoyo ({archivosApoyo.length}/{MAX_ARCHIVOS_APOYO})
                </label>

                {archivosApoyo.map((archivo, index) => (
                    <div key={archivo.IdArchivo ?? archivo.Path ?? index}
                        className="d-flex align-items-center justify-content-between mb-1">
                        <span className="text-truncate" style={{ maxWidth: "80%" }}
                            role="button" title={archivo.OriginalFileName}
                            onClick={() => onDescargarArchivo(archivo)}>
                            <FontAwesomeIcon icon="paperclip" className="mr-2" />
                            <u>{archivo.OriginalFileName}</u>
                        </span>
                        <FontAwesomeIcon icon="trash" role="button" title="Eliminar"
                            style={{ color: "#c00" }}
                            onClick={() => onEliminarArchivo(archivo, index)} />
                    </div>
                ))}

                {archivosApoyo.length < MAX_ARCHIVOS_APOYO
                    ? <GenericUploadFileV2
                        // key fuerza un remonte limpio despues de cada archivo agregado:
                        // GenericUploadFileV2 guarda su propio fileName internamente y
                        // muestra un icono de "eliminar" propio apenas termina de subir
                        // (ver GenericUploadFileV2.js:116-122), que quedaba pegado junto
                        // al boton "Subir archivo" aun despues de que el archivo ya
                        // pasara a la lista de arriba (con su propio tacho) -- dos
                        // iconos de eliminar para el mismo archivo (24/09/2026).
                        key={archivosApoyo.length}
                        uploadFileToS3={onAgregarArchivo}
                        id="convocatoria-archivo-apoyo"
                        textButton="Subir archivo"
                        extensionesValidas={EXTENSIONES_ARCHIVOS_APOYO}
                        resetInitialFile={false}
                        subiendoArchivoPublicState={subiendoArchivo}
                        setSubiendoArchivoPublicState={setSubiendoArchivo} />
                    : <small className="text-muted">
                        Maximo {MAX_ARCHIVOS_APOYO} archivos. Elimine uno para subir otro.
                    </small>
                }
            </div>
        </div>
    </div>
)

// ──────────────────────────────────────────────────────────────────────────
// Paso 2 — Segmentación
// Nota: "Cargo" es single-select (no multi-select) — regla de negocio confirmada:
// una convocatoria segmenta un único cargo mostrado (Profesor=847 o Asistente de
// Educación=648); la expansión a los cargos reales equivalentes la resuelve
// TP_CargoEquivalente del lado del backend, no la UI. Ver modelo-datos-notas.md.
//
// Arriba, recordatorio de solo lectura de lo ya configurado en el paso 1
// (Convocatoria, Tipo, Vigencia) — mismo componente RecapField que usan los
// pasos 3 y 4 (26/08/2026). No se recapitula Cargo/Regiones/Sedes aquí porque
// son justamente los campos editables de este paso.
// ──────────────────────────────────────────────────────────────────────────
const SegmentacionStep = ({ formValues, handleChange, tipoProcesoOptions, cargoOptions, regionOptions, sedeOptions }) => {
    const { convocatoriaLabel, tipoLabel, vigenciaLabel } = getRecapLabels(formValues, {
        tipoProcesoOptions: tipoProcesoOptions, cargoOptions: cargoOptions, regionOptions: regionOptions, sedeOptions: sedeOptions
    });

    // Sedes amarradas a la Region seleccionada (dbo.Sede.IdDepartamento, expuesto
    // como idRegion por GetSedes). Las sedes sin Region asociada (idRegion=null,
    // ej. BACK OFFICE) SIEMPRE se muestran, elijas o no una Region — nunca van a
    // "aparecer" al elegir una Region, asi que quedarian inalcanzables si solo se
    // mostraran cuando no hay ninguna Region seleccionada.
    const sedeOptionsFiltradas = sedeOptions.filter(o =>
        o.idRegion == null || formValues.IdsRegiones?.includes(o.idRegion)
    );

    // Agrupa el multiselect de Sedes por Region (react-select soporta grupos
    // pasando {label, options} en vez de options planas) usando desRegion, que ya
    // viene resuelto en el mismo GetSedes — sin cruzar contra regionOptions ni
    // pedir nada mas al API.
    const sedeOptionsAgrupadas = Object.values(
        sedeOptionsFiltradas.reduce((grupos, sede) => {
            const region = sede.desRegion || "Sin región";
            if (!grupos[region]) grupos[region] = { label: region, options: [] };
            grupos[region].options.push(sede);
            return grupos;
        }, {})
    );

    // Si cambia la seleccion de Regiones y una Sede ya elegida deja de pertenecer
    // a ninguna de las regiones vigentes, se descarta automaticamente (evita que
    // quede guardada una sede "invisible" para el usuario).
    useEffect(() => {
        if (!formValues.IdsSedes?.length) return;
        const idsValidos = sedeOptionsFiltradas.map(o => o.value);
        const idsFiltrados = formValues.IdsSedes.filter(id => idsValidos.includes(id));
        if (idsFiltrados.length !== formValues.IdsSedes.length) {
            handleChange({ target: { name: "IdsSedes", value: idsFiltrados } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.IdsRegiones]);

    const handleSeleccionarTodasLasSedes = () => {
        handleChange({ target: { name: "IdsSedes", value: sedeOptionsFiltradas.map(o => o.value) } });
    }

    const handleDeseleccionarTodasLasSedes = () => {
        handleChange({ target: { name: "IdsSedes", value: [] } });
    }

    return (
    <div>
        <div className="d-flex flex-wrap" style={{ gap: "24px" }}>
            <RecapField label="Convocatoria" value={convocatoriaLabel} />
            <RecapField label="Tipo" value={tipoLabel} />
            <RecapField label="Vigencia" value={vigenciaLabel} />
        </div>

        <hr />

        <div className="row">
        <div className="col-sm-6">
            <InputSelect label="Cargo" name="IdCargo" sectionName={SECTION_NAME}
                options={cargoOptions}
                value={cargoOptions.find(o => o.value === formValues.IdCargo)}
                onChange={handleChange}
                direction="column" isRequired />

            <InputMultiSelect label="Regiones" name="IdsRegiones" sectionName={SECTION_NAME}
                options={regionOptions}
                value={regionOptions.filter(o => formValues.IdsRegiones?.includes(o.value))}
                onChange={handleChange}
                direction="column" isRequired />

            <div className="d-flex justify-content-between align-items-center">
                <label className="modal-label required mb-0">Sedes</label>
                <div>
                    <button type="button" className="btn btn-link p-0" onClick={handleSeleccionarTodasLasSedes}>
                        Seleccionar todos
                    </button>
                    <span className="mx-1">|</span>
                    <button type="button" className="btn btn-link p-0" onClick={handleDeseleccionarTodasLasSedes}>
                        Deseleccionar todos
                    </button>
                </div>
            </div>
            <InputMultiSelect name="IdsSedes" sectionName={SECTION_NAME}
                options={sedeOptionsAgrupadas}
                value={sedeOptionsFiltradas.filter(o => formValues.IdsSedes?.includes(o.value))}
                onChange={handleChange}
                direction="column" />
        </div>

        <div className="col-sm-6">
            <label className="modal-label d-flex required">Antigüedad</label>
            <div className="d-flex align-items-start" style={{ gap: "16px" }}>
                <div style={{ maxWidth: "160px" }}>
                    <InputNumber label="Años" name="AnioAntiguedad" sectionName={SECTION_NAME}
                        value={formValues.AnioAntiguedad}
                        onChange={handleChange}
                        direction="column" isRequired />
                </div>
                <div style={{ maxWidth: "160px" }}>
                    <InputNumber label="Meses" name="MesAntiguedad" sectionName={SECTION_NAME}
                        value={formValues.MesAntiguedad}
                        onChange={handleChange}
                        direction="column" isRequired />
                </div>
            </div>
        </div>
        </div>
    </div>
    )
}
